import { type ApolloQueryResult, useApolloClient } from '@apollo/client';
import { type ModalProps } from 'antd';
import { useMemo, useState, useCallback, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type AddInventoryAuditTrailCommentMutation,
    AddInventoryAuditTrailCommentDocument,
    type AddInventoryAuditTrailCommentMutationVariables,
} from '../../../../api/mutations/addInventoryAuditTrailComment';
import {
    useGetInventoryAuditTrailsQuery,
    type GetInventoryAuditTrailsQuery,
    type GetInventoryAuditTrailsQueryVariables,
} from '../../../../api/queries/getInventoryAuditTrails';
import { pageReducer } from '../../../../components/PaginatedTable';
import InternalErrorResult from '../../../../components/results/InternalErrorResult';
import ActivityLogTable from '../ActivityLogTable';
import CommentModal, { type CommentFormValues } from './CommentModal';
import { StyledContainer, StyledLink, StyledTitle } from './shared';

const useInventoryCommentModal = (
    inventoryId: string,
    refetch: (
        variables?: Partial<GetInventoryAuditTrailsQueryVariables>
    ) => Promise<ApolloQueryResult<GetInventoryAuditTrailsQuery>>
) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const apolloClient = useApolloClient();

    const onSubmit = useCallback(
        async (values: CommentFormValues) => {
            await apolloClient.mutate<
                AddInventoryAuditTrailCommentMutation,
                AddInventoryAuditTrailCommentMutationVariables
            >({
                mutation: AddInventoryAuditTrailCommentDocument,
                variables: {
                    inventoryId,
                    comment: values.comment,
                },
            });

            refetch();
        },
        [apolloClient, inventoryId, refetch]
    );

    const actions = useMemo(
        () => ({
            open: () => setIsModalOpen(true),
            close: () => setIsModalOpen(false),
        }),
        [setIsModalOpen]
    );

    return {
        ...actions,
        render: (props: ModalProps) => (
            <CommentModal {...props} isModalOpen={isModalOpen} onClose={actions.close} onSubmit={onSubmit} />
        ),
    };
};

type InventoryActivityLogWithCommentProps = {
    inventoryId: string;
    timezone: string;
};

const InventoryActivityLogWithComment = ({ inventoryId, timezone }: InventoryActivityLogWithCommentProps) => {
    const { t } = useTranslation(['auditTrails', 'common']);

    const [state, dispatch] = useReducer(pageReducer, { page: 1, pageSize: 10 });
    const { page, pageSize } = state;

    const ModalTitle = <StyledTitle>{t('auditTrails:fields.comment.label')}</StyledTitle>;
    const okText = <div>{t('common:actions.save')}</div>;

    const { data, loading, error, refetch } = useGetInventoryAuditTrailsQuery({
        variables: { inventoryId, pagination: { limit: pageSize, offset: (page - 1) * pageSize } },
    });

    // resolve activity log not refresh issue when go in detail
    useEffect(() => {
        refetch();
    }, []);

    const commentModal = useInventoryCommentModal(inventoryId, refetch);

    if (!loading && error) {
        return <InternalErrorResult />;
    }

    return (
        <>
            <StyledContainer>
                <h1>{t('auditTrails:title')}</h1>
                <StyledLink onClick={commentModal.open}>{t('auditTrails:addComment')}</StyledLink>
            </StyledContainer>
            <ActivityLogTable
                count={data?.result?.count ?? 0}
                data={data?.result?.items ?? []}
                dispatch={dispatch}
                state={state}
                timezone={timezone}
            />
            {commentModal.render({
                okText,
                title: ModalTitle,
            })}
        </>
    );
};

export default InventoryActivityLogWithComment;
